// src/components/FilterSidebar.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Search, X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

import { TeamData, ActiveFilters } from '@dopomogai/types';
import { MarketplaceItem } from '@dopomogai/data';

// A more specific local type for a marketplace agent.
type MarketplaceAgent = MarketplaceItem & {
  itemType: 'agent';
  team: string;
  capabilities: string[];
  integrations: string[];
  status: string;
};


// ===================================================================================
//  INTERNAL SUB-COMPONENT: FilterSection
// ===================================================================================

interface FilterSectionProps {
  title: string;
  options: { value: string; label: string; count: number }[];
  selected: string[];
  onToggle: (value: string) => void;
  activeCount: number;
}

const FilterSection: React.FC<FilterSectionProps> = ({ title, options, selected, onToggle, activeCount }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (options.length === 0) return null;

  return (
    <div className="border-b-2 border-black">
      <button
        className="w-full flex justify-between items-center p-4"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900">{title}</h3>
            {!isOpen && activeCount > 0 && (
                <div className="bg-sky-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {activeCount}
                </div>
            )}
        </div>
        <motion.div animate={{ rotate: isOpen ? 0 : -180 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={20} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 space-y-2">
              {options.map(({ value, label, count }) => {
                const isSelected = selected.includes(value);
                return (
                  <label key={value} className="flex items-center space-x-3 cursor-pointer group">
                    <div className="relative w-5 h-5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggle(value)}
                        className="opacity-0 absolute w-full h-full cursor-pointer"
                      />
                      <div
                        className={clsx(
                          'w-5 h-5 rounded-md border-2 border-black flex items-center justify-center transition-all duration-150',
                          isSelected ? 'bg-emerald-500' : 'bg-white group-hover:bg-slate-100'
                        )}
                      >
                        <AnimatePresence>
                        {isSelected && (
                           <motion.div initial={{scale:0}} animate={{scale:1}} exit={{scale:0}}>
                            <X size={14} className="text-white" strokeWidth={3} />
                           </motion.div>
                        )}
                        </AnimatePresence>
                      </div>
                    </div>
                    <span className="flex-grow text-gray-800 font-medium">{label}</span>
                    <span className="text-xs font-mono bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-md">
                      {count}
                    </span>
                  </label>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// ===================================================================================
//  MAIN COMPONENT: FilterSidebar
// ===================================================================================

interface FilterSidebarProps {
  allItems: MarketplaceItem[];
  allTeams: TeamData[];
  onFilterChange: (filters: ActiveFilters) => void;
  className?: string;
  initialFilters?: ActiveFilters;
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({ allItems, allTeams, onFilterChange, className, initialFilters }) => {
  const { t } = useTranslation('common'); 
  
  // VVVV --- FIX #1 --- VVVV
  // The default state object must include the `type` property to match the ActiveFilters type.
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(initialFilters || {
    query: '', type: [], teams: [], capabilities: [], integrations: [], status: [],
  });
  // ^^^^ --- END OF FIX #1 --- ^^^^

  useEffect(() => {
    const handler = setTimeout(() => {
      onFilterChange(activeFilters);
    }, 300);
    return () => clearTimeout(handler);
  }, [activeFilters, onFilterChange]);


  const filterOptions = useMemo(() => {
    const capabilities = new Map<string, number>();
    const integrations = new Map<string, number>();
    const statuses = new Map<string, number>();

    const agents = allItems.filter(
      (item): item is MarketplaceAgent => item.itemType === 'agent'
    );

    agents.forEach(agent => {
      agent.capabilities?.forEach((cap: string) => capabilities.set(cap, (capabilities.get(cap) || 0) + 1));
      agent.integrations?.forEach((int: string) => integrations.set(int, (integrations.get(int) || 0) + 1));
      if (agent.status) statuses.set(agent.status, (statuses.get(agent.status) || 0) + 1);
    });

    const toOptions = (map: Map<string, number>) =>
      Array.from(map.entries())
        .map(([value, count]) => ({ value, label: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), count }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return {
      type: [
        { value: 'agent', label: 'Individual Agents', count: allItems.filter(i => i.itemType === 'agent').length },
        { value: 'solution', label: 'Multi-Agent Solutions', count: allItems.filter(i => i.itemType === 'solution').length }
      ],
      teams: allTeams
        .map(team => ({ value: team.slug, label: t(team.nameKey, { ns: 'teams_showcase' }), count: agents.filter(a => a.team === team.slug).length }))
        .filter(team => team.count > 0)
        .sort((a,b) => a.label.localeCompare(b.label)),
      capabilities: toOptions(capabilities),
      integrations: toOptions(integrations),
      status: toOptions(statuses),
    };
  }, [allItems, allTeams, t]);


  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActiveFilters(prev => ({ ...prev, query: e.target.value }));
  };

  const handleToggle = (filterKey: keyof Omit<ActiveFilters, 'query'>, value: string) => {
    setActiveFilters(prev => {
      const currentValues = prev[filterKey];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterKey]: newValues };
    });
  };

  const clearFilters = () => {
    // VVVV --- FIX #2 --- VVVV
    // The cleared state object must also include the `type` property.
    const clearedState: ActiveFilters = { query: '', type: [], teams: [], capabilities: [], integrations: [], status: [] };
    // ^^^^ --- END OF FIX #2 --- ^^^^
    setActiveFilters(clearedState);
    onFilterChange(clearedState);
  };
  
  const totalActiveFilters = activeFilters.type.length + activeFilters.teams.length + activeFilters.capabilities.length + activeFilters.integrations.length + activeFilters.status.length + (activeFilters.query ? 1 : 0);

  return (
    <aside className={clsx('bg-white border-2 border-black rounded-xl h-full flex flex-col', className)}>
      <div className="p-4 border-b-2 border-black flex justify-between items-center">
        <div className="flex items-center gap-2">
            <SlidersHorizontal size={20} className="text-gray-700"/>
            <h2 className="text-lg font-bold text-gray-900">Filter</h2>
        </div>
        {totalActiveFilters > 0 && (
             <button onClick={clearFilters} className="text-xs font-semibold text-red-600 hover:underline">
             Clear ({totalActiveFilters})
           </button>
        )}
      </div>

      <div className="p-4 border-b-2 border-black">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or keyword..."
            value={activeFilters.query}
            onChange={handleQueryChange}
            className="w-full pl-10 pr-4 py-2 rounded-lg border-2 border-black bg-slate-50 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
      </div>

      {/* VVVV --- FIX #3 --- VVVV */}
      {/* Added the FilterSection for "Type" and included its active count in totalActiveFilters */}
      <div className="flex-grow overflow-y-auto">
        <FilterSection title="Type" options={filterOptions.type} selected={activeFilters.type} onToggle={(v) => handleToggle('type', v)} activeCount={activeFilters.type.length} />
        <FilterSection title="Team" options={filterOptions.teams} selected={activeFilters.teams} onToggle={(v) => handleToggle('teams', v)} activeCount={activeFilters.teams.length} />
        <FilterSection title="Capability" options={filterOptions.capabilities} selected={activeFilters.capabilities} onToggle={(v) => handleToggle('capabilities', v)} activeCount={activeFilters.capabilities.length} />
        <FilterSection title="Integration" options={filterOptions.integrations} selected={activeFilters.integrations} onToggle={(v) => handleToggle('integrations', v)} activeCount={activeFilters.integrations.length} />
        <FilterSection title="Status" options={filterOptions.status} selected={activeFilters.status} onToggle={(v) => handleToggle('status', v)} activeCount={activeFilters.status.length} />
      </div>
      {/* ^^^^ --- END OF FIX #3 --- ^^^^ */}
    </aside>
  );
};